import React from 'react';
import { 
  FiZoomIn, 
  FiZoomOut, 
  FiRotateCcw,
  FiMaximize2,
  FiMinimize2,
  FiTarget,
  FiMove,
  FiHome,
  FiMapPin,
  FiEdit3,
  FiLayers
} from 'react-icons/fi';
import './DrawingControls.css';

interface DrawingControlsProps {
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetView: () => void;
  onRotate: () => void;
  onToggleFullscreen: () => void;
  isFullscreen: boolean;
  // Zone controls
  onStartZoneCreation?: () => void;
  onStopZoneCreation?: () => void;
  onToggleZoneVisibility?: () => void;
  onEditZones?: () => void;
  isCreatingZone?: boolean;
  showZones?: boolean;
  zoneCount?: number;
  // Defect controls (optional for backward compatibility)
  onStartPlacing?: () => void;
  onStopPlacing?: () => void;
  isPlacingPin?: boolean;
}

const DrawingControls: React.FC<DrawingControlsProps> = ({
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onResetView,
  onRotate,
  onToggleFullscreen,
  isFullscreen,
  // Zone controls
  onStartZoneCreation,
  onStopZoneCreation,
  onToggleZoneVisibility,
  onEditZones,
  isCreatingZone = false,
  showZones = true,
  zoneCount = 0,
  // Defect controls (optional)
  onStartPlacing,
  onStopPlacing,
  isPlacingPin = false
}) => {
  const formatZoomLevel = (zoom: number): string => {
    return `${Math.round(zoom * 100)}%`;
  };

  return (
    <div className="drawing-controls">
      {/* Zoom Controls */}
      <div className="control-group">
        <button
          className="control-btn"
          onClick={onZoomIn}
          title="Zoom In (+ key)"
          disabled={zoomLevel >= 5}
        >
          <FiZoomIn size={14} />
        </button>

        <div className="zoom-display" title={`Current zoom: ${formatZoomLevel(zoomLevel)}`}>
          {formatZoomLevel(zoomLevel)}
        </div>

        <button
          className="control-btn"
          onClick={onZoomOut}
          title="Zoom Out (- key)"
          disabled={zoomLevel <= 0.1}
        >
          <FiZoomOut size={14} />
        </button>
      </div>

      {/* View Controls */}
      <div className="control-group">
        <button
          className="control-btn"
          onClick={onResetView}
          title="Reset View (R key)"
        >
          <FiHome size={14} />
        </button>

        <button
          className="control-btn"
          onClick={onRotate}
          title="Rotate 90°"
        >
          <FiRotateCcw size={14} />
        </button>

        <button
          className="control-btn"
          onClick={onToggleFullscreen}
          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        >
          {isFullscreen ? <FiMinimize2 size={14} /> : <FiMaximize2 size={14} />}
        </button>
      </div>

      {/* Pin Placement Controls - Only show if defect controls are provided */}
      {onStartPlacing && onStopPlacing && (
        <div className="control-group">
          <button
            className={`control-btn pin-btn ${isPlacingPin ? 'active' : ''}`}
            onClick={isPlacingPin ? onStopPlacing : onStartPlacing}
            title={isPlacingPin ? "Cancel Pin Placement (Esc)" : "Place Defect Pin (P key)"}
          >
            {isPlacingPin ? <FiMove size={14} /> : <FiTarget size={14} />}
          </button>

          {isPlacingPin && (
            <span className="pin-instruction">
              Click on drawing to place pin
            </span>
          )}
        </div>
      )}

      {/* Zone Controls */}
      {onStartZoneCreation && (
        <div className="control-group">
          <button
            className={`control-btn zone-btn ${isCreatingZone ? 'active' : ''}`}
            onClick={isCreatingZone ? onStopZoneCreation : onStartZoneCreation}
            title={isCreatingZone ? "Cancel Zone Creation (Esc)" : "Create Polygon Zone (Z key)"}
          >
            <FiMapPin size={14} />
          </button>

          {onToggleZoneVisibility && (
            <button
              className={`control-btn zone-visibility-btn ${showZones ? 'active' : ''}`}
              onClick={onToggleZoneVisibility}
              title={showZones ? "Hide Zones" : "Show Zones"}
            >
              <FiLayers size={14} />
            </button>
          )}

          {onEditZones && (
            <button
              className="control-btn"
              onClick={onEditZones}
              title="Edit Zones"
            >
              <FiEdit3 size={14} />
            </button>
          )}

          {isCreatingZone && (
            <span className="zone-instruction">
              Click for zone points
            </span>
          )}

          {zoneCount > 0 && (
            <span className="zone-count">
              {zoneCount} zone{zoneCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {/* Keyboard Shortcuts Info */}
      <div className="control-group shortcuts-info">
        <div className="shortcuts-tooltip">
          <div className="shortcuts-trigger">?</div>
          <div className="shortcuts-content">
            <h4>Keyboard Shortcuts</h4>
            <div className="shortcut-item">
              <kbd>P</kbd> <span>Place pin</span>
            </div>
            <div className="shortcut-item">
              <kbd>Z</kbd> <span>Create zone</span>
            </div>
            <div className="shortcut-item">
              <kbd>+</kbd> <span>Zoom in</span>
            </div>
            <div className="shortcut-item">
              <kbd>-</kbd> <span>Zoom out</span>
            </div>
            <div className="shortcut-item">
              <kbd>R</kbd> <span>Reset view</span>
            </div>
            <div className="shortcut-item">
              <kbd>Esc</kbd> <span>Cancel action</span>
            </div>
            <div className="shortcut-item">
              <span className="mouse-action">Drag</span> <span>Pan drawing</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrawingControls;